import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Colors,
    Heading,
    Icon,
    Text,
    type ColorType,
    type IconName,
} from '@amelio/ui-library';

import type { ReactNode } from 'react';

interface EmptyStateActionProps {
    label: string;
    onClick: () => void;
    iconName?: IconName;
    /** Greys the button out while its action is still running; it stays in place rather than vanishing. */
    disabled?: boolean;
}

interface EmptyStateProps {
    message: string;
    title?: string;
    action?: EmptyStateActionProps;
    /**
     * A second, lesser way out, drawn in white to the left of `action`. Reading order stays
     * secondary then primary, the way the module's modal footers read.
     */
    secondaryAction?: EmptyStateActionProps;
    /**
     * A third way out, drawn like `secondaryAction` and read before it. For an empty state whose
     * whole purpose is to offer the ways of creating the first fact, and where there are three of
     * them.
     */
    tertiaryAction?: EmptyStateActionProps;
    illustration?: ReactNode;
    className?: string;
}

const actionIcon = ({ iconName }: EmptyStateActionProps, color: ColorType) =>
    iconName ? (
        <Icon
            name={iconName}
            color={color}
            size={'lg'}
        />
    ) : undefined;

export const EmptyState = ({
    message,
    title,
    action,
    secondaryAction,
    tertiaryAction,
    illustration,
    className,
}: EmptyStateProps) => {
    // Plain string concatenation can't reliably let a consumer's own `py-*` win over this
    // default (Tailwind's cascade order isn't the class-attribute order), so the default is
    // dropped outright whenever the caller supplies its own vertical padding instead.
    const hasCustomVerticalPadding = className?.split(/\s+/).some((token) => /^py-/.test(token));

    return (
        <div
            className={`flex flex-col items-center justify-center gap-4 text-center ${hasCustomVerticalPadding ? '' : 'py-12'} ${className ?? ''}`}
        >
            {illustration}

            {title && <Heading level={2}>{title}</Heading>}

            <Text
                color={Colors.BASE_400}
                className={'max-w-2xl'}
            >
                {message}
            </Text>

            {(tertiaryAction || secondaryAction || action) && (
                <div className={'flex flex-wrap items-center justify-center gap-3'}>
                    {[tertiaryAction, secondaryAction]
                        .filter((lesserAction) => lesserAction !== undefined)
                        .map((lesserAction) => (
                            <ButtonSecondary
                                key={lesserAction.label}
                                size={'md'}
                                leftIcon={actionIcon(lesserAction, Colors.BASE)}
                                disabled={lesserAction.disabled}
                                onClick={lesserAction.onClick}
                            >
                                {lesserAction.label}
                            </ButtonSecondary>
                        ))}

                    {action && (
                        <ButtonGradientPrimary
                            size={'md'}
                            leftIcon={actionIcon(action, Colors.WHITE)}
                            disabled={action.disabled}
                            onClick={action.onClick}
                        >
                            {action.label}
                        </ButtonGradientPrimary>
                    )}
                </div>
            )}
        </div>
    );
};
